import { apiPost } from '../config/api';
import { countWordsInNoteJson } from '../utils/pilotMetrics';

function primaryNotePayload(note) {
  return note.openaiNote || note.note_content || null;
}

function primaryNoteType(note) {
  return note.note_type || note.noteType || 'progress_note';
}

export async function persistClinicalDraft(draft, options = {}) {
  const duration = Number(
    options.audioDurationSeconds
    || draft.recordingDurationSeconds
    || 0
  );
  const noteJson = options.noteJson || draft.openaiNote || draft.generatedNotes?.[0]?.note || null;
  const noteType = options.noteType || draft.noteType || draft.generatedNotes?.[0]?.type || 'progress_note';
  const noteGeneratedAt = options.noteGeneratedAt || new Date().toISOString();
  const wordsBeforeEdit = options.wordsBeforeEdit ?? countWordsInNoteJson(noteJson);

  try {
    const session = await apiPost('/api/sessions', {
      anonymous_patient_token: draft.anonymous_patient_token || undefined,
    });
    const recording = await apiPost('/api/recordings', {
      session_id: session.id,
      audio_duration_seconds: Math.max(0, Math.round(duration)),
      transcript: draft.transcriptRaw || draft.transcriptCleaned || draft.transcript || '',
      recording_end_at: options.recordingEndAt || draft.recordingEndAt || noteGeneratedAt,
    });
    const note = await apiPost('/api/notes', {
      recording_id: recording.id,
      session_id: session.id,
      note_type: noteType,
      note_json: noteJson,
      nurse_saved: false,
      note_generated_at: noteGeneratedAt,
      words_before_edit: wordsBeforeEdit,
    });
    return {
      sessionId: session.id,
      recordingId: recording.id,
      noteId: note.id,
      noteGeneratedAt,
      wordsBeforeEdit,
    };
  } catch (err) {
    console.warn('Clinical draft persist skipped', err.message);
    return null;
  }
}

export async function saveClinicalNoteMetrics(clinicalIds, note, metrics = {}) {
  if (!clinicalIds?.noteId) {
    return persistClinicalNote(note, metrics);
  }
  const noteJson = note.openaiNote || note.note_content || null;
  try {
    await apiPost(`/api/notes/${clinicalIds.noteId}/save`, {
      note_json: noteJson,
      was_edited: Boolean(metrics.wasEdited),
      edit_count: Number(metrics.editCount || 0),
      words_before_edit: metrics.wordsBeforeEdit ?? null,
      words_after_edit: metrics.wordsAfterEdit ?? countWordsInNoteJson(noteJson),
      time_to_review_seconds: metrics.timeToReviewSeconds ?? null,
    });
  } catch (err) {
    console.warn('Clinical save skipped', err.message);
  }
}

export async function discardClinicalNote(clinicalIds) {
  if (!clinicalIds?.noteId) return { recordingDiscarded: false };
  try {
    return await apiPost(`/api/notes/${clinicalIds.noteId}/discard`, {});
  } catch (err) {
    console.warn('Clinical note discard skipped', err.message);
    return { recordingDiscarded: false };
  }
}

export async function discardClinicalRecording(recordingId) {
  if (!recordingId) return null;
  try {
    return await apiPost(`/api/recordings/${recordingId}/discard`, {});
  } catch (err) {
    console.warn('Clinical recording discard skipped', err.message);
    return null;
  }
}

export async function persistDiscardedRecordingStub(options = {}) {
  const duration = Math.max(0, Math.round(Number(options.audioDurationSeconds || 0)));
  try {
    const session = await apiPost('/api/sessions', {});
    return await apiPost('/api/recordings', {
      session_id: session.id,
      audio_duration_seconds: duration,
      was_discarded: true,
      recording_end_at: options.recordingEndAt || new Date().toISOString(),
    });
  } catch (err) {
    console.warn('Discarded recording stub skipped', err.message);
    return null;
  }
}

/** Legacy one-shot persist (fallback when no draft ids). */
export async function persistClinicalNote(note, options = {}) {
  if (options.clinicalIds?.noteId) {
    return saveClinicalNoteMetrics(options.clinicalIds, note, options);
  }
  const duration = Number(
    options.audioDurationSeconds
    || note.audio_duration_seconds
    || note.recordingDurationSeconds
    || 0
  );
  const noteJson = primaryNotePayload(note);
  const noteType = primaryNoteType(note);
  const now = new Date().toISOString();
  try {
    const session = await apiPost('/api/sessions', {
      anonymous_patient_token: note.anonymous_patient_token || undefined,
    });
    const recording = await apiPost('/api/recordings', {
      session_id: session.id,
      audio_duration_seconds: Math.max(0, Math.round(duration)),
      transcript: note.originalTranscript || note.cleanedTranscript || '',
      recording_end_at: options.recordingEndAt || now,
    });
    const draft = await apiPost('/api/notes', {
      recording_id: recording.id,
      session_id: session.id,
      note_type: noteType,
      note_json: noteJson,
      nurse_saved: false,
      note_generated_at: options.noteGeneratedAt || now,
      words_before_edit: options.wordsBeforeEdit ?? countWordsInNoteJson(noteJson),
    });
    await saveClinicalNoteMetrics(
      { noteId: draft.id },
      note,
      {
        wasEdited: options.wasEdited,
        editCount: options.editCount,
        wordsBeforeEdit: options.wordsBeforeEdit,
        wordsAfterEdit: options.wordsAfterEdit,
        timeToReviewSeconds: options.timeToReviewSeconds,
      }
    );
  } catch (err) {
    console.warn('Clinical persist skipped', err.message);
  }
}
